/**
 * Button visuals live apart from the component so `<Link>` and `<a>` can wear
 * the same clothes without importing a component that only renders `<button>`.
 */
export type ButtonVariant =
  'primary' | 'secondary' | 'outline' | 'ghost' | 'success' | 'danger' | 'sunny' | 'onDark';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold whitespace-nowrap ' +
  'transition duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-60 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-soft hover:bg-brand-700 hover:shadow-glow',
  // The dark pill from the landing design — the quieter half of a CTA pair.
  secondary: 'bg-accent-900 text-white shadow-soft hover:bg-accent-800',
  outline: 'border border-line bg-white text-ink hover:border-brand-300 hover:bg-brand-50',
  ghost: 'text-ink-soft hover:bg-brand-50 hover:text-ink',
  success: 'bg-safe-600 text-white shadow-soft hover:bg-safe-700',
  // Reserved for raising an alarm and for destructive, hard-to-undo actions —
  // not for ordinary "no" buttons, which are ghost or outline.
  danger: 'bg-red-600 text-white shadow-soft hover:bg-red-700',
  // Sun yellow on ink. Only legible on a dark surface, so it is the "first"
  // button inside the dark closing band and nowhere else.
  sunny: 'bg-sun-400 text-ink shadow-soft hover:bg-sun-500',
  // The outlined half of a CTA pair sitting on a dark or orange band. Kept as
  // its own variant because overriding `outline` with utility classes is a
  // coin-flip — same-specificity utilities resolve by stylesheet order.
  onDark: 'border border-white/40 bg-white/5 text-white hover:border-white hover:bg-white/15',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-3.5 py-2 text-sm',
  md: 'px-5 py-2.5 text-sm sm:text-base',
  lg: 'px-6 py-3.5 text-base',
};

export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  extra = '',
): string {
  return `${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${extra}`.trim();
}
